import React, { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api-request';
import { useUser } from '@/contexts/UserContext';
import { LOGO_UPDATED_EVENT } from '@/lib/file-upload';

/**
 * Sidebar company-logo slot (desktop Sidebar + mobile drawer).
 *
 * Renders nothing at all -- no wrapper div, no reserved space -- when the
 * company has no active logo, so a company without one gets the exact
 * same sidebar layout as before this component existed. Reuses the same
 * `companies_logos` table/bucket and "most recent active row" query
 * CompanyTab.tsx's upload flow already established; this component only
 * reads, it never uploads.
 *
 * Sizing is deliberately NOT a fixed box: the <img> is capped by
 * max-width/max-height and left at `width/height: auto`, so its rendered
 * height is whatever its own aspect ratio produces at that width -- a
 * 120x50 logo ends up short, a 200x200 logo ends up as tall as it is
 * wide, and neither ever gets stretched, cropped, or padded with empty
 * space to fill an artificial box.
 */
export function CompanyLogoBox() {
  const { companyId } = useUser();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const fetchLogo = useCallback(async () => {
    if (!companyId) {
      setLogoUrl(null);
      return;
    }

    try {
      // the newest active logo, with its public URL already resolved by the backend (GET /api/company/logos)
      const logos = await apiRequest<Array<{ public_url: string | null }>>('api/company/logos');
      setLogoUrl(logos[0]?.public_url || null);
    } catch {
      setLogoUrl(null);
    }
  }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchLogo();
    })();

    // CompanyTab.tsx (Settings) lives in a separate, already-mounted part
    // of the tree -- this is how it tells an already-rendered sidebar a
    // new logo was just uploaded (or removed).
    const handleUpdated = () => {
      if (!cancelled) fetchLogo();
    };
    window.addEventListener(LOGO_UPDATED_EVENT, handleUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener(LOGO_UPDATED_EVENT, handleUpdated);
    };
  }, [fetchLogo]);

  if (!logoUrl) {
    return null;
  }

  return (
    <div className="px-3 pt-1.5 pb-0.5 flex justify-center">
      <img
        src={logoUrl}
        alt="Company logo"
        style={{
          maxWidth: '100%',
          maxHeight: '96px',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </div>
  );
}

export default CompanyLogoBox;
